import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {type VirtuosoHandle} from 'react-virtuoso';

import {type SectionWithRef} from '../types';

type PanelBankSectionsScrollContextProviderProps = {
  children: React.ReactNode;
};

type PanelBankSectionsScrollContextType = {
  scrollToStableSectionIdRef: React.MutableRefObject<string | undefined>;
};

const PanelBankSectionsScrollContext = createContext<
  PanelBankSectionsScrollContextType | undefined
>(undefined);

export const PanelBankSectionsScrollContextProvider = ({
  children,
}: PanelBankSectionsScrollContextProviderProps) => {
  const scrollToStableSectionIdRef = useRef<string | undefined>(undefined);

  const value = useMemo(
    () => ({
      scrollToStableSectionIdRef,
    }),
    [scrollToStableSectionIdRef]
  );

  return (
    <PanelBankSectionsScrollContext.Provider value={value}>
      {children}
    </PanelBankSectionsScrollContext.Provider>
  );
};

export const usePanelBankSectionsScrollContext = () => {
  const value = useContext(PanelBankSectionsScrollContext);

  if (value == null) {
    throw new Error(
      'usePanelBankSectionsScrollContext must be used within a PanelBankSectionsScrollContext'
    );
  }

  return value;
};

export const useScrollToStableSectionId = (
  virtuosoRef: React.MutableRefObject<VirtuosoHandle | null>,
  visibleSections: SectionWithRef[]
) => {
  const {scrollToStableSectionIdRef} = usePanelBankSectionsScrollContext();
  useEffect(() => {
    if (scrollToStableSectionIdRef.current && virtuosoRef.current) {
      const sectionIndex = visibleSections.findIndex(
        section => section.__id__ === scrollToStableSectionIdRef.current
      );
      if (sectionIndex !== -1) {
        // Wait for the layout shift before sccrolling.
        requestAnimationFrame(() => {
          virtuosoRef.current?.scrollToIndex({
            index: sectionIndex,
            align: 'start',
          });
          scrollToStableSectionIdRef.current = undefined;
        });
      }
    }
  }, [scrollToStableSectionIdRef, virtuosoRef, visibleSections]);
};
